import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../../App";
import { useBusinessUnits } from "../../hooks/useBusinessUnits";
import {
  Search, Calendar, MapPin, Users, FileText, MessageCircle,
  CreditCard, FolderOpen, Plus, Filter, Sparkles, Clock, ChevronRight, Edit2, Trash2, Building2
} from "lucide-react";

const ESTATUS_META = {
  nuevo: { label: "Nuevo", cls: "eo-pill-info" },
  cotizado: { label: "Cotizado", cls: "eo-pill-info" },
  contratado: { label: "Contratado", cls: "eo-pill-accent" },
  no_confirmado: { label: "No confirmado", cls: "eo-pill-warning" },
  confirmado: { label: "Confirmado", cls: "eo-pill-success" },
  produccion: { label: "Producción", cls: "eo-pill-warning" },
  en_produccion: { label: "Producción", cls: "eo-pill-warning" },
  en_evento: { label: "En evento", cls: "eo-pill-primary" },
  finalizado: { label: "Finalizado", cls: "eo-pill-muted" },
  cancelado: { label: "Cancelado", cls: "eo-pill-danger" },
};

const FILTROS = ["todos", "nuevo", "cotizado", "contratado", "no_confirmado", "confirmado", "produccion", "en_evento", "finalizado", "cancelado"];

const AgendaEventos = () => {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const unidadFiltro = params.get("unidad") || "";  // slug de unidad participante ("petit", "bocatto"…) o vacío = todas
  const { unidades: unidadesDisponibles } = useBusinessUnits();
  const [eventos, setEventos] = useState([]);
  const [clientes, setClientes] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filtro, setFiltro] = useState("todos");

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [eRes, cRes] = await Promise.all([
        axios.get(`${API}/eventos`),
        axios.get(`${API}/clientes`),
      ]);
      setEventos(eRes.data || []);
      const cMap = {};
      (cRes.data || []).forEach(c => { cMap[c.cliente_id] = c; });
      setClientes(cMap);
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const hoy = useMemo(() => { const d = new Date(); d.setHours(0,0,0,0); return d; }, []);
  const manana = useMemo(() => { const d = new Date(hoy); d.setDate(d.getDate()+1); return d; }, [hoy]);
  const finSemana = useMemo(() => { const d = new Date(hoy); d.setDate(d.getDate()+(7-hoy.getDay())); return d; }, [hoy]);

  const parseFecha = (f) => { if (!f) return null; const d = new Date(f); d.setHours(0,0,0,0); return d; };

  const filtrados = useMemo(() => eventos
    .filter(ev => {
      // ── Filtro por UNIDAD PARTICIPANTE (fuente única `evento.unidades`) ──
      if (unidadFiltro) {
        const us = Array.isArray(ev.unidades) ? ev.unidades.map(s => String(s).toLowerCase()) : [];
        if (!us.includes(unidadFiltro.toLowerCase())) return false;
      }
      if (filtro !== "todos" && ev.estatus !== filtro) return false;
      if (!search) return true;
      const cl = clientes[ev.cliente_id];
      const txt = `${ev.nombre} ${ev.tipo} ${cl?.nombre || ""} ${cl?.whatsapp || ""} ${ev.fecha || ""}`.toLowerCase();
      return txt.includes(search.toLowerCase());
    })
    .sort((a, b) => (a.fecha || "").localeCompare(b.fecha || "")), [eventos, clientes, filtro, search, unidadFiltro]);

  const grupos = useMemo(() => {
    const g = { hoy: [], manana: [], esta_semana: [], proximos: [], anteriores: [] };
    filtrados.forEach(ev => {
      const f = parseFecha(ev.fecha);
      if (!f) { g.proximos.push(ev); return; }
      if (f.getTime() === hoy.getTime()) g.hoy.push(ev);
      else if (f.getTime() === manana.getTime()) g.manana.push(ev);
      else if (f > manana && f <= finSemana) g.esta_semana.push(ev);
      else if (f > finSemana) g.proximos.push(ev);
      else g.anteriores.push(ev);
    });
    return g;
  }, [filtrados, hoy, manana, finSemana]);

  const totalActivos = grupos.hoy.length + grupos.manana.length + grupos.esta_semana.length + grupos.proximos.length;

  return (
    <div className="max-w-6xl mx-auto space-y-6 pb-6">
      {/* HERO */}
      <div className="eo-animate-in">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <p className="text-[11px] uppercase tracking-[0.18em] mb-2" style={{ color: "var(--brand-primary)" }}>
              Sistema Operativo de Eventos
            </p>
            <h1 className="font-display text-3xl sm:text-4xl md:text-5xl leading-none" style={{ color: "var(--brand-text)" }}>
              {unidadFiltro
                ? `Agenda · ${unidadesDisponibles.find(u => u.slug === unidadFiltro)?.nombre || unidadFiltro}`
                : "Agenda"}
            </h1>
            <p className="mt-2 text-sm" style={{ color: "var(--brand-text-muted)" }}>
              {totalActivos} eventos activos · {unidadFiltro ? "filtrado por unidad participante" : "todo nace y regresa al evento"}
            </p>
          </div>
          <button onClick={() => navigate("/panel/cotizar")} className="eo-btn eo-btn-primary" data-testid="btn-nuevo-evento">
            <Plus className="w-4 h-4" /> Nuevo evento
          </button>
        </div>

        {/* Sub-nav de UNIDADES PARTICIPANTES (usa la misma fuente `evento.unidades`) */}
        <div className="mt-4 flex gap-2 flex-wrap items-center" data-testid="agenda-unidades-nav">
          <span className="text-[10px] uppercase tracking-[0.18em] mr-1" style={{ color: "var(--brand-text-muted)" }}>Vista:</span>
          <button
            onClick={() => { const p = new URLSearchParams(params); p.delete("unidad"); setParams(p); }}
            data-testid="agenda-unidad-general"
            className="px-3 py-1.5 rounded-full text-xs font-medium border"
            style={{
              background: !unidadFiltro ? "var(--brand-primary)" : "transparent",
              color: !unidadFiltro ? "#fff" : "var(--brand-text-muted)",
              borderColor: !unidadFiltro ? "var(--brand-primary)" : "var(--brand-border)",
            }}
          >General</button>
          {unidadesDisponibles.filter(u => u.activa !== false).map(u => {
            const activo = unidadFiltro === u.slug;
            return (
              <button key={u.slug}
                onClick={() => { const p = new URLSearchParams(params); p.set("unidad", u.slug); setParams(p); }}
                data-testid={`agenda-unidad-${u.slug}`}
                className="px-3 py-1.5 rounded-full text-xs font-medium border flex items-center gap-1"
                style={{
                  background: activo ? (u.color || "var(--brand-primary)") : (u.color_soft || "transparent"),
                  color: activo ? "#fff" : (u.color_dark || "var(--brand-text-muted)"),
                  borderColor: activo ? (u.color || "var(--brand-primary)") : "var(--brand-border)",
                }}
              >
                <Building2 className="w-3 h-3" /> {u.nombre}
              </button>
            );
          })}
        </div>

        {/* Stats compactas */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 sm:gap-3 mt-4 sm:mt-6">
          <StatCard label="Hoy" value={grupos.hoy.length} accent="primary" />
          <StatCard label="Mañana" value={grupos.manana.length} accent="accent" />
          <StatCard label="Esta semana" value={grupos.esta_semana.length} accent="info" />
          <StatCard label="Próximos" value={grupos.proximos.length} accent="muted" />
        </div>
      </div>

      {/* SEARCH + FILTER */}
      <div className="eo-card p-3 flex flex-col md:flex-row gap-2 items-stretch md:items-center">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: "var(--brand-text-muted)" }} />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar cliente, evento o teléfono…"
            className="eo-input pl-10"
            data-testid="search-agenda"
          />
        </div>
        <div className="flex gap-1 overflow-x-auto pb-1 md:pb-0 -mx-1 px-1">
          {FILTROS.map(f => (
            <button
              key={f}
              onClick={() => setFiltro(f)}
              className="px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition border"
              style={{
                background: filtro === f ? "var(--brand-primary)" : "var(--brand-surface-card)",
                color: filtro === f ? "#fff" : "var(--brand-text-muted)",
                borderColor: filtro === f ? "var(--brand-primary)" : "var(--brand-border)",
              }}
              data-testid={`filtro-${f}`}
            >
              {f === "todos" ? "Todos" : (ESTATUS_META[f]?.label || f)}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: "var(--brand-primary)", borderTopColor: "transparent" }} />
        </div>
      ) : eventos.length === 0 ? (
        <EmptyState onCreate={() => navigate("/panel/cotizar")} />
      ) : (
        <div className="space-y-6">
          <Grupo titulo="Hoy" eventos={grupos.hoy} clientes={clientes} navigate={navigate} destacar />
          <Grupo titulo="Mañana" eventos={grupos.manana} clientes={clientes} navigate={navigate} />
          <Grupo titulo="Esta semana" eventos={grupos.esta_semana} clientes={clientes} navigate={navigate} />
          <Grupo titulo="Próximos" eventos={grupos.proximos} clientes={clientes} navigate={navigate} />
          {grupos.anteriores.length > 0 && <Grupo titulo="Anteriores" eventos={grupos.anteriores} clientes={clientes} navigate={navigate} colapsado />}
        </div>
      )}
    </div>
  );
};

const StatCard = ({ label, value, accent = "muted" }) => {
  const accentMap = {
    primary: { bg: "var(--brand-primary-soft)", color: "var(--brand-primary-dark)" },
    accent: { bg: "var(--brand-accent-soft)", color: "#8C6735" },
    info: { bg: "var(--brand-info-soft)", color: "var(--brand-info)" },
    muted: { bg: "var(--brand-surface-muted)", color: "var(--brand-text-muted)" },
  }[accent];
  return (
    <div className="eo-card p-4">
      <p className="text-[11px] uppercase tracking-[0.14em]" style={{ color: "var(--brand-text-muted)" }}>{label}</p>
      <div className="flex items-baseline gap-2 mt-1">
        <span className="font-display text-3xl" style={{ color: "var(--brand-text)" }}>{value}</span>
        <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: accentMap.bg, color: accentMap.color }}>eventos</span>
      </div>
    </div>
  );
};

const Grupo = ({ titulo, eventos, clientes, navigate, destacar, colapsado }) => {
  const [abierto, setAbierto] = useState(!colapsado);
  if (eventos.length === 0) return null;
  return (
    <section className="eo-animate-in">
      <button onClick={() => setAbierto(a => !a)} className="w-full flex items-center justify-between mb-3 group" data-testid={`grupo-${titulo.toLowerCase().replace(/ /g, "-")}`}>
        <div className="flex items-center gap-3">
          {destacar && <span className="w-2 h-2 rounded-full" style={{ background: "var(--brand-primary)" }} />}
          <h2 className="font-display text-2xl" style={{ color: destacar ? "var(--brand-primary-dark)" : "var(--brand-text)" }}>{titulo}</h2>
          <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: "var(--brand-surface-muted)", color: "var(--brand-text-muted)" }}>{eventos.length}</span>
        </div>
        <ChevronRight className="w-4 h-4 transition-transform" style={{ color: "var(--brand-text-muted)", transform: abierto ? "rotate(90deg)" : "rotate(0deg)" }} />
      </button>
      {abierto && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {eventos.map(ev => <EventoCard key={ev.evento_id} evento={ev} cliente={clientes[ev.cliente_id]} navigate={navigate} />)}
        </div>
      )}
    </section>
  );
};

const EventoCard = ({ evento, cliente, navigate }) => {
  const meta = ESTATUS_META[evento.estatus] || ESTATUS_META.nuevo;
  const totalInv = (parseInt(evento.adultos) || 0) + (parseInt(evento.ninos) || 0);
  const saldo = evento.saldo ?? Math.max((evento.total || 0) - (evento.total_pagado || 0), 0);
  const fechaCorta = evento.fecha ? new Date(evento.fecha + "T00:00:00").toLocaleDateString("es-MX", { day: "2-digit", month: "short" }) : "—";

  return (
    <div className="eo-card eo-card-hover overflow-hidden cursor-pointer" onClick={() => navigate(`/panel/eventos/${evento.evento_id}`)} data-testid={`card-evento-${evento.evento_id}`}>
      {/* Top: fecha + estatus */}
      <div className="p-4 pb-3 flex items-start justify-between gap-2">
        <div className="flex items-center gap-3 min-w-0 flex-1">
          <div className="w-12 h-12 rounded-2xl flex flex-col items-center justify-center text-white shrink-0" style={{ background: `linear-gradient(135deg, var(--brand-primary), var(--brand-primary-dark))` }}>
            <span className="text-[9px] uppercase tracking-wider leading-none opacity-90">{fechaCorta.split(" ")[1] || ""}</span>
            <span className="font-display text-base leading-none mt-0.5">{fechaCorta.split(" ")[0] || "—"}</span>
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="font-display text-lg leading-tight truncate" style={{ color: "var(--brand-text)" }}>{evento.nombre || "Evento"}</h3>
            <p className="text-xs truncate mt-0.5" style={{ color: "var(--brand-text-muted)" }}>{cliente?.nombre || "Sin cliente"}</p>
          </div>
        </div>
        <span className={`eo-pill ${meta.cls} shrink-0`}>{meta.label}</span>
      </div>

      {/* Mid: info */}
      <div className="px-4 pb-3 space-y-1.5 text-xs" style={{ color: "var(--brand-text-muted)" }}>
        {evento.hora_inicio && (
          <div className="flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" /> <span>{evento.hora_inicio}{evento.hora_fin ? ` – ${evento.hora_fin}` : ""}</span></div>
        )}
        {evento.ubicacion && (() => {
          const isUrl = /^https?:\/\//i.test(String(evento.ubicacion).trim());
          const lugar = evento.lugar_nombre || evento.nombre_lugar;
          return (
            <div className="flex items-center gap-1.5 min-w-0">
              <MapPin className="w-3.5 h-3.5 shrink-0" />
              {isUrl ? (
                <a href={evento.ubicacion} target="_blank" rel="noopener noreferrer"
                   className="truncate underline-offset-2 hover:underline"
                   style={{ color: "var(--brand-primary-dark)" }}
                   onClick={(e) => e.stopPropagation()}>
                  {lugar || "Abrir ubicación"}
                </a>
              ) : (
                <span className="truncate">{lugar || evento.ubicacion}</span>
              )}
            </div>
          );
        })()}
        <div className="flex items-center gap-1.5"><Users className="w-3.5 h-3.5" /> <span>{totalInv} invitados</span></div>
        {Array.isArray(evento.unidades) && evento.unidades.length > 0 && (
          <div className="flex items-center gap-1 flex-wrap pt-1" data-testid={`agenda-badges-${evento.evento_id}`}>
            {evento.unidades.map(u => (
              <span key={u} className="text-[9px] uppercase tracking-wide px-1.5 py-0.5 rounded-full border" style={{ background: "var(--brand-surface-card)", borderColor: "var(--brand-border)", color: "var(--brand-text-muted)" }}>
                {u}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Saldo */}
      <div className="px-4 py-2.5 flex items-center justify-between text-xs" style={{ borderTop: "1px solid var(--brand-border)", background: "var(--brand-surface-muted)" }}>
        <span className="uppercase tracking-wider text-[10px]" style={{ color: "var(--brand-text-muted)" }}>Saldo</span>
        <span className="font-display text-base" style={{ color: saldo > 0 ? "var(--brand-warning)" : "var(--brand-success)" }}>
          ${saldo.toLocaleString("es-MX")}
        </span>
      </div>

      {/* Actions */}
      <div className="grid grid-cols-6 divide-x" style={{ borderTop: "1px solid var(--brand-border)", borderColor: "var(--brand-border)" }} onClick={(e) => e.stopPropagation()}>
        <QuickBtn icon={FolderOpen} label="Abrir" onClick={() => navigate(`/panel/eventos/${evento.evento_id}`)} tid={`btn-abrir-${evento.evento_id}`} />
        <QuickBtn icon={FileText} label="Docs" onClick={() => navigate(`/panel/eventos/${evento.evento_id}?tab=documentos`)} tid={`btn-docs-${evento.evento_id}`} />
        <QuickBtn icon={MessageCircle} label="WhatsApp" onClick={() => { const tel = (cliente?.whatsapp || "").replace(/\D/g, ""); const t = tel.length === 10 ? `52${tel}` : tel; if (t) window.open(`https://wa.me/${t}`, "_blank"); }} tid={`btn-wa-${evento.evento_id}`} />
        <QuickBtn icon={CreditCard} label="Pagos" onClick={() => navigate(`/panel/eventos/${evento.evento_id}?tab=pagos`)} tid={`btn-pag-${evento.evento_id}`} />
        <QuickBtn icon={Edit2} label="Editar" onClick={() => navigate(`/panel/eventos/${evento.evento_id}?edit=1`)} tid={`btn-edit-evt-${evento.evento_id}`} />
        <QuickBtn icon={Trash2} label="Eliminar" danger onClick={async () => {
          if (!window.confirm(`¿Eliminar el evento "${evento.nombre}"?\n\nEsta acción archiva el evento y libera la fecha (no se pierde el historial).`)) return;
          try {
            await axios.delete(`${API}/eventos/${evento.evento_id}`);
            toast.success("Evento eliminado. Recargando...");
            setTimeout(() => window.location.reload(), 500);
          } catch (err) {
            toast.error(err?.response?.data?.detail || "No se pudo eliminar");
          }
        }} tid={`btn-del-evt-${evento.evento_id}`} />
      </div>
    </div>
  );
};

const QuickBtn = ({ icon: Icon, label, onClick, danger, tid }) => (
  <button onClick={onClick} data-testid={tid} className={`flex flex-col items-center gap-0.5 py-2.5 transition ${danger ? "hover:bg-red-50" : "hover:bg-[var(--brand-primary-soft)]"}`} title={label}>
    <Icon className="w-3.5 h-3.5" style={{ color: danger ? "#dc2626" : "var(--brand-primary)" }} strokeWidth={1.75} />
    <span className="text-[9px]" style={{ color: danger ? "#dc2626" : "var(--brand-text-muted)" }}>{label}</span>
  </button>
);

const EmptyState = ({ onCreate }) => (
  <div className="eo-card p-12 text-center">
    <div className="w-16 h-16 mx-auto rounded-2xl flex items-center justify-center mb-4" style={{ background: "var(--brand-primary-soft)" }}>
      <Calendar className="w-7 h-7" style={{ color: "var(--brand-primary)" }} />
    </div>
    <h3 className="font-display text-2xl" style={{ color: "var(--brand-text)" }}>Aún sin eventos</h3>
    <p className="text-sm mt-1 mb-4" style={{ color: "var(--brand-text-muted)" }}>Empieza creando una cotización rápida y se generará el primer evento.</p>
    <button onClick={onCreate} className="eo-btn eo-btn-primary inline-flex">
      <Sparkles className="w-4 h-4" /> Cotizar evento
    </button>
  </div>
);

export default AgendaEventos;
